import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { GoogleLogin } from '@react-oauth/google'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/context/AuthContext'
import { useRedirectTarget } from '@/hooks/useRedirectTarget'

const schema = z.object({
  email: z.string().email('有効なメールアドレスを入力してください'),
  password: z.string().min(8, 'パスワードは8文字以上にしてください'),
  password2: z.string(),
}).refine((d) => d.password === d.password2, {
  message: 'パスワードが一致しません',
  path: ['password2'],
})

type FormValues = z.infer<typeof schema>

export default function RegisterPage() {
  const { register: registerUser, googleLogin, isAuthenticated, isLoading: authLoading } = useAuth()
  const location = useLocation()
  const from = useRedirectTarget()
  const [isLoading, setIsLoading] = useState(false)

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  })

  // 認証状態の確定待ち（トークン保持者の一瞬のフォームちらつきを防ぐ）。
  if (authLoading) return null
  // 登録（または Google ログイン）成功で認証済みになったら宣言的に遷移する。
  // 全 Hook 呼び出しの後に置く（Hooks 規約）。
  if (isAuthenticated) return <Navigate to={from} replace />

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true)
    try {
      await registerUser(values.email, values.password, values.password2)
      // 遷移は isAuthenticated を見た宣言的リダイレクトに任せる
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg ?? '登録に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSuccess = async (credential: string) => {
    try {
      await googleLogin(credential)
      // 遷移は isAuthenticated を見た宣言的リダイレクトに任せる
    } catch {
      toast.error('Googleログインに失敗しました')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-1">
          <h1 className="text-2xl font-bold">新規登録</h1>
          <p className="text-sm text-muted-foreground">アカウントを作成してはじめましょう</p>
        </div>

        <div className="flex justify-center scale-90 origin-center">
          <GoogleLogin
            onSuccess={(res) => {
              if (res.credential) handleGoogleSuccess(res.credential)
            }}
            onError={() => toast.error('Googleログインに失敗しました')}
            width={360}
            text="signup_with"
          />
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground">または</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">メールアドレス</label>
            <Input {...register('email')} type="email" placeholder="example@email.com" autoComplete="email" />
            {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">パスワード（8文字以上）</label>
            <Input {...register('password')} type="password" placeholder="パスワード" autoComplete="new-password" />
            {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">パスワード（確認）</label>
            <Input {...register('password2')} type="password" placeholder="パスワードをもう一度" autoComplete="new-password" />
            {errors.password2 && <p className="text-xs text-destructive">{errors.password2.message}</p>}
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? '登録中...' : 'アカウントを作成'}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          すでにアカウントをお持ちの方は{' '}
          <Link to="/login" state={location.state} className="text-primary underline underline-offset-2">
            ログイン
          </Link>
        </p>
      </div>
    </div>
  )
}
